import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { FileMagnifyingGlass, HourglassMedium, UserFocus, BellRinging, CircleNotch } from "@phosphor-icons/react";

const CAT = { Schedule_C: "Schedule C substantiation", Schedule_E: "Rental (Schedule E) records", "K-1": "Incoming K-1s" };

export function GapAnalytics() {
  const [nudging, setNudging] = useState(null);
  const nudge = useMutation({
    mutationFn: (docType) => api.post("/analytics/nudge", { doc_type: docType }),
    onSuccess: ({ data }) => {
      setNudging(null);
      if (data.nudged === 0 && data.skipped_recent > 0) toast.info(`All ${data.skipped_recent} stalled client(s) were already nudged within the last 7 days`);
      else toast.success(`Nudged ${data.nudged} client${data.nudged === 1 ? "" : "s"}${data.skipped_recent ? ` (${data.skipped_recent} skipped — nudged recently)` : ""}`);
    },
    onError: (e) => { setNudging(null); toast.error(errText(e)); },
  });
  const { data } = useQuery({ queryKey: ["gap-analytics"], queryFn: () => api.get("/analytics/gaps").then((r) => r.data), refetchInterval: 60000 });
  if (!data) return null;
  const stalls = data.checklist_stalls || [];
  const maxWait = Math.max(...stalls.map((s) => s.waiting_count), 1);
  return (
    <div data-testid="gap-analytics">
      <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide mb-4">Document chasing intelligence — where clients stall</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="stall-doc-types">
          <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><FileMagnifyingGlass size={15} /> Most-stalled document types</h3>
          <div className="space-y-2.5">
            {stalls.slice(0, 8).map((s) => (
              <div key={s.doc_type} data-testid={`stall-${s.doc_type}`} title={`Waiting on: ${(s.clients || []).join(", ")}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-700">{s.doc_type}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-zinc-500">{s.client_count} client{s.client_count > 1 ? "s" : ""} · ~{s.avg_days_waiting}d</span>
                    <button data-testid={`nudge-all-${s.doc_type}`} title={`Nudge every client stalled on ${s.doc_type}`}
                      disabled={nudge.isPending} onClick={() => { setNudging(s.doc_type); nudge.mutate(s.doc_type); }}
                      className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150">
                      {nudge.isPending && nudging === s.doc_type ? <CircleNotch size={12} className="animate-spin" /> : <BellRinging size={12} />}
                    </button>
                  </span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className={`h-full rounded-sm ${s.avg_days_waiting >= 30 ? "bg-rose-500" : s.avg_days_waiting >= 14 ? "bg-amber-500" : "bg-zinc-900"}`} style={{ width: `${Math.round((s.waiting_count / maxWait) * 100)}%` }} />
                </div>
              </div>
            ))}
            {stalls.length === 0 && <p className="text-xs text-zinc-400 italic">Nothing outstanding — every checklist is complete.</p>}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="cross-year-stats">
          <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><HourglassMedium size={15} /> Cross-year gap categories</h3>
          <div className="space-y-2">
            {(data.cross_year || []).map((c) => (
              <div key={c.category} className="rounded-md border border-zinc-100 px-3 py-2">
                <p className="text-xs text-zinc-800">{CAT[c.category] || c.category}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">
                  <span className={c.open > 0 ? "text-amber-600 font-medium" : ""}>{c.open} open</span> · {c.noted} explained · {c.reminded} nudged{c.open > 0 && ` · avg ${c.avg_days_open}d open`}
                </p>
              </div>
            ))}
            {(data.cross_year || []).length === 0 && <p className="text-xs text-zinc-400 italic">No prior-year profiles imported yet.</p>}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="slowest-clients">
          <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><UserFocus size={15} /> Slowest responders</h3>
          <div className="space-y-1.5">
            {(data.slowest_clients || []).map((c) => (
              <div key={c.client_id} className="flex items-center justify-between text-xs border-b border-zinc-100 last:border-0 py-1.5">
                <span className="text-zinc-700">{c.client_name}</span>
                <span className="font-mono text-[10px] text-zinc-500">{c.open_gaps} open · {c.max_days_open}d oldest</span>
              </div>
            ))}
            {(data.slowest_clients || []).length === 0 && <p className="text-xs text-zinc-400 italic">No clients sitting on open gaps.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
